/**
 * AI Generate Dropdown – group selector + amount + mode + generate button
 * Uses Radix Popover portal to escape overflow containers and show correctly.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Sparkles, ChevronDown, Layers, Copy, ChevronRight } from 'lucide-react';

import type { CountryAiMode } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/shared/multiCountryBulkEdit.types';
import { Button, LoadingSpinner, Checkbox, useModalPortalSurface } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { useTextPromptGroups } from '../hooks/useTextPromptGroups';
import { useGenerateTexts } from '../hooks/useGenerateTexts';
import {
  getActiveSelectableLeafGroups,
  optionLabelForGroup,
  type TreeGroupLike,
} from '../utils/group-tree.utils';
import { AiPerDropDefaultGroupPanel } from './AiPerDropDefaultGroupPanel';
import { PerDropGroupSelector, type DropLabel } from './PerDropGroupSelector';

export type AiGenerateMode = 'same' | 'perDrop';
export interface AiGeneratedTextMetadata {
  aiTextGroupId?: string | null;
  aiTextGroupName?: string | null;
  aiTextRoleId?: string | null;
  aiTextRoleName?: string | null;
}

interface AiGenerateDropdownProps {
  countryKey: string;
  /** When > 1, shows mode toggle: same for all vs generate per drop. */
  dropCount?: number;
  /** Optional labels per drop (list name + id). Scale = drop id, bulk edit = draft id. */
  dropLabels?: DropLabel[];
  /** Existing batches from partial run – only generate for missing drops on retry. */
  existingBatches?: string[][];
  /** Called when the dropdown opens (e.g. to collapse sibling sections like Senders). */
  onOpen?: () => void;
  /** Called when generation starts or stops (for blocking modal close). */
  onGeneratingChange?: (isGenerating: boolean) => void;
  /** When set, parent runs `/generate` (e.g. multi-country orchestrator); avoids duplicate API calls. */
  delegateGeneration?: boolean;
  creativeScope?: CountryAiMode;
  delegatedBusy?: boolean;
  onDelegatedGenerate?: (p: {
    groupId: string;
    amount: number;
    translate: boolean;
    groupIdsPerDrop?: string[];
  }) => void | Promise<void>;
  onGenerated?: (
    texts: string[] | string[][],
    groupId: string,
    mode: AiGenerateMode,
    durationMs?: number,
    translations?: string[] | string[][],
    groupIdsPerDrop?: string[],
    amountPerDrop?: number,
    aiTextMetadata?: AiGeneratedTextMetadata | AiGeneratedTextMetadata[],
  ) => void;
}

const MASTER_ID = 'master';
const AMOUNTS = Array.from({ length: 15 }, (_, i) => i + 1);

function resolveAiTextMetadata(
  selectedGroupId: string,
  groups?: TreeGroupLike[],
): AiGeneratedTextMetadata {
  const id = selectedGroupId.trim() || MASTER_ID;
  if (id === MASTER_ID) {
    return {
      aiTextGroupId: null,
      aiTextGroupName: null,
      aiTextRoleId: MASTER_ID,
      aiTextRoleName: 'Master prompt',
    };
  }
  const byId = new Map((groups ?? []).map((g) => [g.id, g]));
  const role = byId.get(id);
  if (!role) return { aiTextRoleId: id, aiTextRoleName: id };
  const folder = role.parent_id ? byId.get(role.parent_id) : null;
  return {
    aiTextGroupId: folder?.id ?? null,
    aiTextGroupName: folder?.name ?? null,
    aiTextRoleId: role.id,
    aiTextRoleName: role.name,
  };
}

export function AiGenerateDropdown({
  countryKey,
  dropCount = 1,
  dropLabels,
  existingBatches,
  onOpen,
  onGeneratingChange,
  delegateGeneration = false,
  creativeScope,
  delegatedBusy = false,
  onDelegatedGenerate,
  onGenerated,
}: AiGenerateDropdownProps) {
  const { data: groups } = useTextPromptGroups();
  const generate = useGenerateTexts();
  const selectableLeaves = useMemo(
    () => (groups ? getActiveSelectableLeafGroups(groups) : []),
    [groups],
  );
  const modalPortalSurface = useModalPortalSurface();
  const { warning: toastWarning, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [groupId, setGroupId] = useState(MASTER_ID);
  const [amount, setAmount] = useState(15);
  const [mode, setMode] = useState<AiGenerateMode>(() => (dropCount > 1 ? 'perDrop' : 'same'));
  const [translate, setTranslate] = useState(false);
  const [groupIdsPerDrop, setGroupIdsPerDrop] = useState<string[]>([]);
  const [showPerDropConfig, setShowPerDropConfig] = useState(false);

  const isMulti = dropCount > 1;
  const perDropGroups = useMemo(
    () => isMulti && (delegateGeneration ? creativeScope === 'per_drop' : mode === 'perDrop'),
    [isMulti, delegateGeneration, creativeScope, mode],
  );
  const showApplyToToggle = isMulti && !delegateGeneration;

  useEffect(() => {
    if (!groups?.length || groupId === MASTER_ID) return;
    if (!selectableLeaves.some((g) => g.id === groupId)) setGroupId(MASTER_ID);
  }, [groups, groupId, selectableLeaves]);

  const showApplyDefaultGroupToAllRows = useMemo(() => {
    if (!isMulti || dropCount < 1) return false;
    return [...Array(dropCount)].some(
      (_, i) => (groupIdsPerDrop[i] ?? groupId) !== groupId,
    );
  }, [isMulti, dropCount, groupIdsPerDrop, groupId]);

  const coerceGroupIdsPerDrop = useCallback(
    (ids: string[]) => {
      const valid = new Set([MASTER_ID, ...selectableLeaves.map((g) => g.id)]);
      return ids.map((id) => (valid.has(id) ? id : MASTER_ID));
    },
    [selectableLeaves],
  );

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (v) onOpen?.();
      setOpen(v);
    },
    [onOpen],
  );

  useEffect(() => {
    if (!perDropGroups) return;
    setGroupIdsPerDrop((prev) =>
      coerceGroupIdsPerDrop(
        Array.from({ length: dropCount }, (_, i) => prev[i] ?? groupId),
      ),
    );
  }, [dropCount, perDropGroups, groupId, coerceGroupIdsPerDrop]);

  useEffect(() => {
    if (!perDropGroups) setShowPerDropConfig(false);
  }, [perDropGroups]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const isBusy = delegateGeneration ? delegatedBusy : isGenerating || generate.isPending;

  useEffect(() => {
    onGeneratingChange?.(isBusy);
    return () => onGeneratingChange?.(false);
  }, [isBusy, onGeneratingChange]);

  const handleGenerate = useCallback(async () => {
    if (!groupId) return;
    const start = performance.now();
    const runPerDrop = isMulti && (delegateGeneration ? creativeScope === 'per_drop' : mode === 'perDrop');

    if (delegateGeneration && onDelegatedGenerate && creativeScope != null) {
      if (runPerDrop) {
        const rawIds =
          groupIdsPerDrop.length === dropCount ? groupIdsPerDrop : Array(dropCount).fill(groupId);
        const ids = coerceGroupIdsPerDrop(rawIds);
        await onDelegatedGenerate({ groupId, amount, translate, groupIdsPerDrop: ids });
      } else {
        await onDelegatedGenerate({ groupId, amount, translate });
      }
      setOpen(false);
      return;
    }

    if (!onGenerated) return;

    if (runPerDrop) {
      setIsGenerating(true);
      const rawIds =
        groupIdsPerDrop.length === dropCount ? groupIdsPerDrop : Array(dropCount).fill(groupId);
      const ids = coerceGroupIdsPerDrop(rawIds);
      const existing = existingBatches?.length === dropCount ? existingBatches : undefined;
      const toGenerate = existing
        ? ids.map((_, i) => !(existing[i]?.length ?? 0)).map((need, i) => (need ? i : -1)).filter((i) => i >= 0)
        : ids.map((_, i) => i);
      const total = toGenerate.length;
      setProgress({ current: 0, total });
      try {
        let completed = 0;
        const settled = await Promise.allSettled(
          toGenerate.map(async (dropIdx) => {
            const gId = ids[dropIdx] ?? groupId;
            const data = await generate.mutateAsync({
              groupId: gId,
              countryKey,
              amount,
              translate,
            });
            completed++;
            setProgress({ current: completed, total });
            return { dropIdx, data };
          }),
        );
        const batches: string[][] = Array.from(
          { length: dropCount },
          (_, i) => existing?.[i] ?? [],
        );
        const trans: (string[] | null)[] | undefined = translate
          ? Array.from({ length: dropCount }, () => null as string[] | null)
          : undefined;
        for (let i = 0; i < settled.length; i++) {
          const s = settled[i];
          const dropIdx = toGenerate[i]!;
          if (s.status === 'fulfilled') {
            batches[dropIdx] = s.value.data.texts;
            if (trans && s.value.data.translations) trans[dropIdx] = s.value.data.translations;
          } else {
            batches[dropIdx] = existing?.[dropIdx] ?? [];
          }
        }
        const failed = settled.filter((s) => s.status === 'rejected');
        if (failed.length) {
          // OpenRouter failures (credits exhausted, rate limit, provider down) carry operator copy.
          const reason = (failed[0] as PromiseRejectedResult).reason;
          toastError(
            `Generation failed for ${failed.length} of ${settled.length} drops`,
            getApiErrorMessage(reason, 'The AI request failed.'),
          );
        }
        if (batches.every((b) => b.length === 0)) {
          if (!failed.length) {
            toastWarning(
              'No texts generated',
              'The AI returned no texts for any drop. Try again, lower the amount, or pick a different group.',
            );
          }
          return;
        }
        const durationMs = Math.round(performance.now() - start);
        const transResult: string[][] | undefined = trans?.some((t) => t?.length)
          ? trans.map((t) => t ?? [])
          : undefined;
        onGenerated(
          batches,
          groupId,
          'perDrop',
          durationMs,
          transResult,
          ids,
          amount,
          ids.map((id) => resolveAiTextMetadata(id, groups)),
        );
        setOpen(false);
      } finally {
        setIsGenerating(false);
        setProgress(null);
      }
    } else {
      generate.mutate(
        { groupId, countryKey, amount, translate },
        {
          onSuccess: (data) => {
            if (!data.texts.length) {
              toastWarning(
                'No texts generated',
                'The AI returned no texts. Try again, lower the amount, or pick a different group.',
              );
              return;
            }
            const durationMs = Math.round(performance.now() - start);
            onGenerated(
              data.texts,
              groupId,
              'same',
              durationMs,
              data.translations,
              undefined,
              undefined,
              resolveAiTextMetadata(groupId, groups),
            );
            setOpen(false);
          },
          onError: (err) => toastError('Generation failed', getApiErrorMessage(err, 'The AI request failed.')),
        },
      );
    }
  }, [
    groupId,
    countryKey,
    amount,
    mode,
    dropCount,
    isMulti,
    translate,
    groupIdsPerDrop,
    existingBatches,
    generate,
    onGenerated,
    coerceGroupIdsPerDrop,
    delegateGeneration,
    creativeScope,
    onDelegatedGenerate,
    groups,
    toastWarning,
    toastError,
  ]);

  const totalTexts = isMulti && perDropGroups ? amount * dropCount : amount;
  const buttonLabel =
    isMulti && perDropGroups
      ? `Generate ${dropCount} × ${amount} texts`
      : `Generate ${totalTexts} text${totalTexts !== 1 ? 's' : ''}`;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="ai-generate-trigger"
          className={cn('h-7 gap-1 text-xs shrink-0', isBusy && 'pointer-events-none')}
          disabled={isBusy}
        >
          {isBusy ? (
            <LoadingSpinner className="h-3.5 w-3.5" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          AI
          <ChevronDown className="h-3 w-3" />
        </Button>
      </Popover.Trigger>

      <Popover.Portal {...(modalPortalSurface ? { container: modalPortalSurface } : {})}>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-[300] w-[min(540px,var(--radix-popover-content-available-width))] max-w-[95vw] rounded-lg border bg-card p-4 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150"
        >
          <div className="space-y-3">
            {!(perDropGroups && showPerDropConfig) && (
              <div>
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Group
                </label>
                <select
                  className="h-8 w-full rounded-md border border-border bg-background px-2.5 text-xs"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value={MASTER_ID}>Default</option>
                  {selectableLeaves.map((g) => (
                    <option key={g.id} value={g.id}>
                      {groups ? optionLabelForGroup(g, groups) : g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <label
                  htmlFor="amount"
                  className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Amount
                </label>
                <select
                  id="amount"
                  className="h-7 w-14 shrink-0 rounded-md border border-border bg-background px-2 text-xs"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                >
                  {AMOUNTS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Checkbox
                  id="translate"
                  checked={translate}
                  onCheckedChange={(v) => setTranslate(v === true)}
                />
                <label
                  htmlFor="translate"
                  className="cursor-pointer select-none text-[10px] text-muted-foreground hover:text-foreground"
                >
                  Translate (EN)
                </label>
              </div>
            </div>
            {translate && (
              <p className="text-[10px] text-amber-600 dark:text-amber-500">
                Translation will take ~2× longer.
              </p>
            )}
            {showApplyToToggle && (
              <div>
                <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Apply to
                </label>
                <div className="flex gap-1 rounded-md border border-border bg-muted/30 p-0.5">
                  <button
                    type="button"
                    onClick={() => setMode('same')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors',
                      mode === 'same'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Copy className="h-3 w-3" />
                    Same for all
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('perDrop')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded py-1.5 text-xs font-medium transition-colors',
                      mode === 'perDrop'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Layers className="h-3 w-3" />
                    Per drop
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {mode === 'same'
                    ? `${amount} texts shared across ${dropCount} drops`
                    : `${amount} unique texts for each of ${dropCount} drops`}
                </p>
              </div>
            )}
            {perDropGroups && groups && (
              <div className={showApplyToToggle ? 'mt-2' : ''}>
                <button
                  type="button"
                  onClick={() => setShowPerDropConfig((v) => !v)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-2.5 py-1.5 text-left text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                >
                  <span className="flex items-center gap-1.5">
                    <Layers className="h-3 w-3" />
                    {showPerDropConfig ? 'Hide group per drop' : 'Configure group per drop'}
                  </span>
                  <ChevronRight
                    className={cn('h-3 w-3 shrink-0 transition-transform', showPerDropConfig && 'rotate-90')}
                  />
                </button>
                {showPerDropConfig && (
                  <div className="mt-2 space-y-2">
                    <AiPerDropDefaultGroupPanel
                      masterId={MASTER_ID}
                      groupId={groupId}
                      onGroupIdChange={setGroupId}
                      activeGroups={selectableLeaves}
                      groupLabelLookup={(g) =>
                        groups
                          ? optionLabelForGroup(g as TreeGroupLike, groups as TreeGroupLike[])
                          : g.name
                      }
                      showApplyToAll={showApplyDefaultGroupToAllRows}
                      onApplyToAll={() =>
                        setGroupIdsPerDrop(Array.from({ length: dropCount }, () => groupId))
                      }
                    />
                    <PerDropGroupSelector
                      dropCount={dropCount}
                      groupIdsPerDrop={groupIdsPerDrop}
                      defaultGroupId={groupId}
                      groups={groups}
                      dropLabels={dropLabels}
                      onChange={setGroupIdsPerDrop}
                    />
                  </div>
                )}
              </div>
            )}
            <Button
              size="sm"
              className="h-8 w-full gap-1.5 text-xs"
              onClick={handleGenerate}
              disabled={
                !groupId ||
                isBusy ||
                (delegateGeneration && (!onDelegatedGenerate || creativeScope == null))
              }
            >
              {isBusy ? (
                <LoadingSpinner className="h-3 w-3 shrink-0" />
              ) : (
                <Sparkles className="h-3 w-3 shrink-0" />
              )}
              {!delegateGeneration && progress ? (
                <span data-testid="ai-generate-progress" className="flex items-center gap-2">
                  <span className="text-primary-foreground/90">Generating</span>
                  <span
                    key={progress.current}
                    className="inline-flex min-w-[3ch] items-center justify-center rounded-md border border-primary-foreground/30 bg-primary-foreground px-1.5 py-0.5 font-bold tabular-nums text-primary animate-in fade-in duration-200"
                  >
                    {progress.current}
                  </span>
                  <span className="text-primary-foreground/90">of {progress.total}</span>
                </span>
              ) : (
                buttonLabel
              )}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
